/**
 * Client-side image quality pre-check — Session 2A.
 *
 * Runs BEFORE a Gemini call is spent. A rejected image never reaches the
 * server, so it never counts against the guest scan cap.
 *
 * Thresholds tuned against 20 REAL handheld nutrition-label photos
 * (see /app/backend/scripts + fixtures/real_labels). Reject rate on the
 * real set: 0/20. Deliberately loose — a rejected good photo is worse
 * than a loose accept; Gemini's own quality report is the backstop.
 *
 * Algorithm (mirrored 1:1 by the Python tuning replica):
 *   downscale to max 320px → grayscale (0.299/0.587/0.114) →
 *   4-neighbour Laplacian variance (blur) + mean luminance (darkness).
 */

export const QUALITY_THRESHOLDS = {
  BLUR_MIN_LAPVAR: 30, // all 20 real photos ≥ 108; gaussian-blurred ≤ 19
  DARK_MIN_LUMINANCE: 40, // darkest readable real photo = 67
  RES_MIN_MAX_DIM: 350, // OFF 400px display images read fine
};

const MESSAGES = {
  blur: "Hold steady and try again",
  resolution: "Move closer to the label",
  darkness: "Too dark — try better light",
};

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export async function checkImageQuality(dataUrl) {
  try {
    const img = await loadImage(dataUrl);
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    if (Math.max(W, H) < QUALITY_THRESHOLDS.RES_MIN_MAX_DIM) {
      return { ok: false, reason: "resolution", message: MESSAGES.resolution };
    }
    const scale = Math.min(1, 320 / Math.max(W, H));
    const w = Math.max(3, Math.round(W * scale));
    const h = Math.max(3, Math.round(H * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const gray = new Float64Array(w * h);
    let lumSum = 0;
    for (let i = 0; i < w * h; i++) {
      const g = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
      gray[i] = g;
      lumSum += g;
    }
    const meanLum = lumSum / (w * h);
    if (meanLum < QUALITY_THRESHOLDS.DARK_MIN_LUMINANCE) {
      return { ok: false, reason: "darkness", message: MESSAGES.darkness };
    }

    let sum = 0;
    let sumSq = 0;
    const n = (w - 2) * (h - 2);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const i = y * w + x;
        const L = 4 * gray[i] - gray[i - w] - gray[i + w] - gray[i - 1] - gray[i + 1];
        sum += L;
        sumSq += L * L;
      }
    }
    const mean = sum / n;
    const lapVar = sumSq / n - mean * mean;
    if (lapVar < QUALITY_THRESHOLDS.BLUR_MIN_LAPVAR) {
      return { ok: false, reason: "blur", message: MESSAGES.blur };
    }
    return { ok: true, metrics: { lapVar, meanLum, maxDim: Math.max(W, H) } };
  } catch {
    // Fail open — never block a scan because the pre-check itself broke.
    return { ok: true, failOpen: true };
  }
}
